
import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { DollarSign, Home, AlertCircle, CheckCircle, Activity, FileText, Wrench, ShieldAlert, Zap, ArrowRight, FileCheck, Key, Building, X } from 'lucide-react';
import { ActivityLog, Workflow, Unit, Receivable, Bill, UserRole, isGlobalPlatformOperatorRole } from '@repo/shared-types';
import { useAppStore } from '../../store';
import { ROUTES } from '@repo/shared-types/routeRegistry';
import { useTheme } from './ThemeContext';
import { FilterBar } from '../ui/FilterBar';
// Import Queries
import { useBills, useReceivables, useThreeWayMatches, useFinancialCloseBlockers } from '../../hooks/queries/useAccounting';
import { useUnits, useProperties } from '../../hooks/queries/useProperties';
import { useTickets } from '../../hooks/queries/useMaintenance';
import { useWorkflows } from '../../hooks/queries/useWorkflows';
import { useActivityLogs } from '../../hooks/queries/useActivity';
import { useApplications, useLeases, useLeasesPaginated } from '../../hooks/queries/useLeasing';
import { useHCVPrograms } from '../../hooks/queries/useCompliance';
import { useGovernanceLogs } from '../../hooks/queries/useGovernance';
import { useInspections } from '../../hooks/queries/useOperations';
import { useDashboardTrends } from '../../hooks/queries/useDashboardTrends';
import { useTasks } from '../../hooks/queries/useTasks';
import { EmptyState } from '../ui/EmptyState';
import { Skeleton } from '../ui/LoadingState';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { useNavigate } from '../../routes';
import { useNavigateTo } from '@/utils/typeSafeNavigation';
import { useNavigateWithReturn } from '@/utils/navigationHelpers';
import { formatCurrency } from '@repo/shared-utils';
import { getAppNow, isDemoMode } from '@/utils/demoClock';
import { useSmoPropertyScopeFilters } from '../../hooks/useSmoPropertyScope';
import { chartColors, getChartTheme } from '@/utils/chartTheme';
import { StatCard } from '../ui/StatCard';
import { buildOpenExceptionQueuePartitions, countOpenExceptions } from '../operations/exceptionQueueBuilders';
import { useSystemHealthIntegrityExceptions } from '../../hooks/queries/useSystemHealth';
import { useQueryClient } from '@tanstack/react-query';
import { AddPropertyWizard } from '../property/AddPropertyWizard';
import { PORTAL_EMPTY_STATES } from '@/constants/portalEmptyStates';

// StatCardProps is now imported from ../ui/StatCard

// StatCard component removed - importing from ui/StatCard

const ActivityItem: React.FC<{ log: ActivityLog; onClick?: () => void }> = ({ log, onClick }) => {
  const getIcon = () => {
    switch (log.type) {
      case 'Financial': return <DollarSign size={16} />;
      case 'Maintenance': return <Wrench size={16} />;
      case 'Compliance': return <ShieldAlert size={16} />;
      case 'Leasing': return <FileText size={16} />;
      default: return <Activity size={16} />;
    }
  };

  const getStyle = () => {
    switch (log.type) {
      case 'Financial': return 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400';
      case 'Maintenance': return 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400';
      case 'Compliance': return 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400';
      case 'Leasing': return 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400';
      default: return 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400';
    }
  };

  return (
    <div
      className={`flex items-start py-5 px-6 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition border-b border-slate-50 dark:border-slate-800 last:border-0 group ${onClick ? 'cursor-pointer active:scale-[0.98] active:shadow-inner active:bg-slate-100/30 focus-visible:ring-4 focus-visible:ring-brand-500/10 focus-visible:scale-[1.005] focus-visible:shadow-sm outline-none' : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      } : undefined}
    >
      <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-4 flex-shrink-0 ${getStyle()}`}>
        {getIcon()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-start gap-2 mb-1">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <h4 className="text-sm font-bold text-slate-900 dark:text-white truncate group-hover:text-brand-600 dark:group-hover:text-brand-400 transition">{log.title}</h4>
            {(log.ledgerVerified || log.feedSource === 'ledger_audit') && (
              <span
                className="flex-shrink-0 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border border-emerald-300 dark:border-emerald-600 bg-emerald-50 dark:bg-emerald-950/50 text-emerald-800 dark:text-emerald-300"
                title="Recorded in the financial ledger audit trail"
              >
                Ledger
              </span>
            )}
          </div>
          <span className="text-xs text-slate-400 whitespace-nowrap ml-2">{log.timestamp}</span>
        </div>
        <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{log.description}</p>
        {log.status === 'Warning' && (
          <span className="inline-block mt-2 px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-900/50">Action Required</span>
        )}
      </div>
    </div>
  );
};

const BRAND_COLOR = chartColors.brand500;
const ACCENT_COLOR = chartColors.purple500;
const COLORS = [BRAND_COLOR, chartColors.slate400, chartColors.amber500];
const DARK_COLORS = [BRAND_COLOR, chartColors.slate700, chartColors.amber500];

const GETTING_STARTED_KEY = 'getting-started-pm-dismissed';
const FIRST_RUN_WIZARD_DISMISSED_KEY = 'first-run-wizard-dismissed';

export const DashboardView: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const navigateTo = useNavigateTo();
  const navigateWithReturn = useNavigateWithReturn();
  const { userRole, currentUserId } = useAppStore();
  const smoScopeFilters = useSmoPropertyScopeFilters();
  const smoOpsScopeFilters = useMemo(
    () => (smoScopeFilters ? { propertyScope: 'self-managed' } : undefined),
    [smoScopeFilters],
  );
  const [gettingStartedDismissed, setGettingStartedDismissed] = useState(false);
  const [firstRunWizardOpen, setFirstRunWizardOpen] = useState(false);
  const [firstRunWizardDismissed, setFirstRunWizardDismissed] = useState(true);
  useEffect(() => {
    if (!currentUserId) return;
    const run = () => {
      try {
        setGettingStartedDismissed(localStorage.getItem(`${GETTING_STARTED_KEY}-${currentUserId}`) === 'true');
        setFirstRunWizardDismissed(localStorage.getItem(`${FIRST_RUN_WIZARD_DISMISSED_KEY}-${currentUserId}`) === 'true');
      } catch {
        setGettingStartedDismissed(false);
        setFirstRunWizardDismissed(false);
      }
    };
    queueMicrotask(run);
  }, [currentUserId]);
  const queryClient = useQueryClient();
  const dismissGettingStarted = useCallback(() => {
    try {
      localStorage.setItem(`${GETTING_STARTED_KEY}-${currentUserId}`, 'true');
      setGettingStartedDismissed(true);
    } catch {
      setGettingStartedDismissed(true);
    }
  }, [currentUserId]);

  // React Query Hooks
  const {
    data: bills,
    isLoading: loadingBills,
    isError: billsError,
    refetch: refetchBills,
  } = useBills(smoOpsScopeFilters);
  const {
    data: receivables,
    isLoading: loadingReceivables,
    isError: receivablesError,
    refetch: refetchReceivables,
  } = useReceivables(smoOpsScopeFilters);
  const { data: units, isLoading: loadingUnits } = useUnits(smoOpsScopeFilters);
  const {
    data: properties = [],
    isLoading: loadingProperties,
    isFetching: fetchingProperties,
    isError: propertiesError,
    isSuccess: propertiesSuccess,
  } = useProperties(smoOpsScopeFilters);
  const isPMOrAdmin = userRole === UserRole.PROPERTY_MANAGER || isGlobalPlatformOperatorRole(userRole);
  // True empty portfolio = we successfully fetched AND got back 0 properties.
  // (loadingProperties or an error must NOT register as "empty" — that's the
  // race that flashes the "Add your first property" CTA on every refresh.)
  const propertiesKnownEmpty =
    propertiesSuccess && !loadingProperties && !fetchingProperties && !propertiesError && properties.length === 0;
  const showGettingStarted =
    userRole === UserRole.PROPERTY_MANAGER && !!currentUserId && propertiesKnownEmpty && !gettingStartedDismissed;
  useEffect(() => {
    // In demo mode the seeded TestDB always has properties, but if a data-loading
    // hiccup (transient RLS, 401 during bridge, etc.) returns an empty array we must not trap the
    // demoer in the first-run wizard. Production PMs still get the wizard on their empty first login.
    if (isDemoMode()) return;
    if (isPMOrAdmin && propertiesKnownEmpty && !firstRunWizardDismissed) {
      queueMicrotask(() => setFirstRunWizardOpen(true));
    }
  }, [isPMOrAdmin, propertiesKnownEmpty, firstRunWizardDismissed]);
  const { data: tickets, isLoading: loadingTickets } = useTickets(smoOpsScopeFilters);
  const { data: workflows, isLoading: isLoadingWorkflows } = useWorkflows();
  const { data: activityLogs = [], isLoading: loadingActivityLogs } = useActivityLogs();
  const { data: applications = [], isLoading: loadingApplications } = useApplications();
  const { data: leases = [], isLoading: loadingLeases } = useLeases();
  const { data: activeLeasesPage } = useLeasesPaginated({ tab: 'Active', page: '1', pageSize: '1' });
  const { data: dashboardTrends, isLoading: loadingDashboardTrends } = useDashboardTrends();
  const { data: hcvPrograms = [], isLoading: loadingHCVPrograms } = useHCVPrograms();
  const { data: governanceLogs = [] } = useGovernanceLogs();
  const { data: inspections = [] } = useInspections();
  const { data: tasks = [], isLoading: loadingTasks } = useTasks();
  const { data: threeWayMatches = [] } = useThreeWayMatches();
  const { data: financialBlockers = [] } = useFinancialCloseBlockers();
  const dashboardDefaultOrgId = useMemo(() => {
    for (const property of properties) {
      const orgId = property.operatingOrganizationId?.trim();
      if (orgId) return orgId;
    }
    return '';
  }, [properties]);
  const { data: integrityExceptionDtos = [] } = useSystemHealthIntegrityExceptions(
    dashboardDefaultOrgId ? { operatingOrganizationId: dashboardDefaultOrgId } : undefined,
    { enabled: Boolean(dashboardDefaultOrgId) },
  );

  const [dateRange, setDateRange] = useState('YTD');
  const [searchTerm, setSearchTerm] = useState('');
  const [now, setNow] = useState(0);
  useEffect(() => {
    const id = setTimeout(() => setNow(Date.now()), 0);
    return () => clearTimeout(id);
  }, []);
  const formatCompactCurrency = useCallback(
    (value: number) =>
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        notation: 'compact',
        maximumFractionDigits: 1,
      }).format(value / 100),
    []
  );

  // Logs to diagnose empty states (removed - use logger if needed for debugging)
  useEffect(() => {
    // Data loading states and counts logging removed
  }, [bills, receivables, units, tickets, workflows, loadingBills, loadingReceivables, loadingUnits, loadingTickets, isLoadingWorkflows]);

  // Safe Accessors
  const safeWorkflows = Array.isArray(workflows) ? workflows : [];
  const activeWorkflows = safeWorkflows.filter((w: Workflow) => w.status === 'Active');

  // Date range filter helper. Boundaries are computed in UTC because ledger
  // rows are stored at UTC midnight (e.g. "2026-01-01T00:00:00Z"); using
  // `new Date(year, 0, 1)` (local midnight) drops those rows for any viewer
  // west of UTC — which is how "Total Revenue" was collapsing to $401.
  const getDateRangeFilter = useCallback((date: Date): boolean => {
    // `getAppNow()` pins to SEED_ANCHOR_DATE in demo mode so YTD/QTD/30D line
    // up with the deterministic seed snapshot instead of the viewer's wall
    // clock (which would drift months past the last seeded receivable).
    const now = getAppNow();
    const y = now.getUTCFullYear();
    const m = now.getUTCMonth();
    const startOfYear = new Date(Date.UTC(y, 0, 1));
    const startOfQuarter = new Date(Date.UTC(y, Math.floor(m / 3) * 3, 1));

    const thirtyDaysAgo = new Date(Date.UTC(y, m, now.getUTCDate() - 30));
    const sixMonthsAgo = new Date(Date.UTC(y, m - 6, 1));
    const twelveMonthsAgo = new Date(Date.UTC(y, m - 12, 1));
    const eighteenMonthsAgo = new Date(Date.UTC(y, m - 18, 1));

    switch (dateRange) {
      case 'YTD':
        return date >= startOfYear;
      case 'QTD':
        return date >= startOfQuarter;
      case '30D':
        return date >= thirtyDaysAgo;
      case '6M':
        return date >= sixMonthsAgo;
      case '12M':
        return date >= twelveMonthsAgo;
      case '18M':
        return date >= eighteenMonthsAgo;
      default:
        return true;
    }
  }, [dateRange]);

  // Dynamic Financial Calculations with date range filtering
  const financialData = useMemo(() => {
    const monthlyData: Record<string, { income: number, expense: number }> = {};

    // Ensure arrays exist before iterating
    const safeReceivables = Array.isArray(receivables) ? receivables : [];
    const safeBills = Array.isArray(bills) ? bills : [];

    safeReceivables.forEach((r: Receivable) => {
      if (!r.date) return;
      const d = new Date(r.date);
      if (isNaN(d.getTime())) return;
      if (!getDateRangeFilter(d)) return;

      const _monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const yearMonth = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!monthlyData[yearMonth]) monthlyData[yearMonth] = { income: 0, expense: 0 };
      monthlyData[yearMonth].income += (r.amountCents ?? 0) / 100;
    });

    safeBills.forEach((b: Bill) => {
      if (!b.date) return;
      const d = new Date(b.date);
      if (isNaN(d.getTime())) return;
      if (!getDateRangeFilter(d)) return;

      // Include bills that have been incurred (Approved, Paid, Overdue) - exclude Pending Approval
      // For historical months, we want to show expenses that were actually incurred
      const billStatus = b.status || 'Approved';
      if (billStatus === 'Pending Approval' && d.getTime() > getAppNow().getTime() - 30 * 24 * 60 * 60 * 1000) {
        // Skip very recent pending bills (within last 30 days) as they may not be incurred yet
        return;
      }

      const yearMonth = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!monthlyData[yearMonth]) monthlyData[yearMonth] = { income: 0, expense: 0 };
      monthlyData[yearMonth].expense += (b.amountCents ?? 0) / 100;
    });

    // Sort by year-month and format for display
    const sortedKeys = Object.keys(monthlyData).sort((a, b) => a.localeCompare(b));
    const firstYear = sortedKeys.length > 0 ? Number.parseInt(sortedKeys[0].split('-')[0]) : null;
    const lastYear = sortedKeys.length > 0 ? Number.parseInt(sortedKeys[sortedKeys.length - 1].split('-')[0]) : null;
    const spansMultipleYears = firstYear !== null && lastYear !== null && (lastYear - firstYear > 0 || sortedKeys.length > 12);

    return sortedKeys.map(key => {
      const [year, month] = key.split('-');
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthName = monthNames[Number.parseInt(month) - 1];
      // Show year if data spans multiple years or more than 12 months
      const displayName = spansMultipleYears ? `${monthName} ${year}` : monthName;
      return {
        name: displayName,
        value: monthlyData[key].income,
        value2: monthlyData[key].expense
      };
    });
  }, [bills, receivables, getDateRangeFilter]);

  // Dynamic Occupancy
  const occupancyData = useMemo(() => {
    const safeUnits = Array.isArray(units) ? units : [];
    if (safeUnits.length === 0) return [];

    const occupied = safeUnits.filter((u: Unit) => u.status === 'Occupied').length;
    const vacant = safeUnits.filter((u: Unit) => u.status === 'Vacant').length;
    const maintenance = safeUnits.filter((u: Unit) => u.status === 'Renovation').length;
    return [
      { name: 'Occupied', value: occupied },
      { name: 'Vacant', value: vacant },
      { name: 'Maintenance', value: maintenance }
    ];
  }, [units]);

  // Safe Aggregations
  const safeBills = useMemo(() => Array.isArray(bills) ? bills : [], [bills]);
  const safeReceivables = useMemo(() => Array.isArray(receivables) ? receivables : [], [receivables]);
  const safeUnits = useMemo(() => Array.isArray(units) ? units : [], [units]);
  const safeTickets = useMemo(() => Array.isArray(tickets) ? tickets : [], [tickets]);
  const safeApplications = useMemo(() => Array.isArray(applications) ? applications : [], [applications]);
  const safeLeases = useMemo(() => Array.isArray(leases) ? leases : [], [leases]);
  const safeTasks = useMemo(() => Array.isArray(tasks) ? tasks : [], [tasks]);

  // Filter revenue by date range
  const filteredReceivables = safeReceivables.filter((r) => {
    if (!r.date) return false;
    const d = new Date(r.date);
    return !isNaN(d.getTime()) && getDateRangeFilter(d);
  });
  const totalRevenue = filteredReceivables.reduce((sum, r) => sum + (r.amountCents ?? 0) / 100, 0);
  const occupancyRate = safeUnits.length > 0
    ? Number(((safeUnits.filter((u) => u.status === 'Occupied').length / safeUnits.length) * 100).toFixed(1))
    : 0;
  const openTickets = safeTickets.filter((t) => t.status !== 'Completed').length;
  const completedTickets = safeTickets.filter((t) => t.status === 'Completed').length;
  const pendingApplications = safeApplications.filter((a) => a.status === 'Pending' || a.status === 'Under Review' || a.status === 'Approved - Pending Lease').length;
  const activeLeases =
    activeLeasesPage?.total ?? safeLeases.filter((l) => l.status === 'Active').length;
  const todayIso = getAppNow().toISOString().split('T')[0];
  const openTaskItems = safeTasks.filter((task) => task.status !== 'Completed');
  const dueTodayTasks = openTaskItems.filter((task) => (task.due || '').slice(0, 10) <= todayIso).length;
  const overdueFollowups = openTaskItems.filter((task) => (task.due || '').slice(0, 10) < todayIso).length;
  const threeWayDiscrepancies = (threeWayMatches ?? []).filter((match) => match.status === 'discrepancy').length;
  const receivableExceptions = safeReceivables.filter((r) => r.status === 'Late' || r.status === 'Unpaid').length;
  const overdueBillsCount = safeBills.filter((bill) => bill.status === 'Overdue').length;
  const hcvAtRiskCount = hcvPrograms.filter((program) => program.complianceStatus === 'At Risk').length;
  const inspectionFlaggedCount = (inspections ?? []).filter((i) => i.status === 'Flagged').length;
  const exceptionCount = useMemo(() => {
    const partitions = buildOpenExceptionQueuePartitions({
      bills: safeBills,
      receivables: safeReceivables,
      matches: threeWayMatches ?? [],
      financialBlockers,
      inspections: inspections ?? [],
      hcvPrograms,
      governanceLogs,
      systemHealthRows: integrityExceptionDtos,
    });
    return countOpenExceptions(partitions).total;
  }, [
    safeBills,
    safeReceivables,
    threeWayMatches,
    financialBlockers,
    inspections,
    hcvPrograms,
    governanceLogs,
    integrityExceptionDtos,
  ]);
  const inspectionExceptions = hcvAtRiskCount + inspectionFlaggedCount;

  const closeChecklistGates = [
    { label: 'No overdue bills', pass: safeBills.filter((bill) => bill.status === 'Overdue').length === 0 },
    { label: 'No 3-way discrepancies', pass: threeWayDiscrepancies === 0 },
    { label: 'Collections risk under control', pass: receivableExceptions <= 2 },
    { label: 'Compliance blockers resolved', pass: inspectionExceptions === 0 },
  ];
  const closeReadiness = Math.round((closeChecklistGates.filter((gate) => gate.pass).length / closeChecklistGates.length) * 100);
  const avgQueueAgingDays = useMemo(() => {
    if (openTaskItems.length === 0 || now === 0) return 0;
    const t = now;
    return Math.max(
      0,
      Math.round(
        openTaskItems.reduce((sum, task) => {
          const due = new Date(task.due);
          if (Number.isNaN(due.getTime())) return sum;
          const delta = (t - due.getTime()) / (1000 * 60 * 60 * 24);
          return sum + Math.max(0, delta);
        }, 0) / openTaskItems.length
      )
    );
  }, [openTaskItems, now]);
  const followupAutomationCoverage = activeWorkflows.length === 0
    ? 0
    : Math.round(
      (activeWorkflows.filter((workflow) => {
        const title = workflow.title.toLowerCase();
        return title.includes('follow') || title.includes('renewal') || title.includes('late fee');
      }).length / activeWorkflows.length) * 100
    );

  const getTrendChange = useCallback((trendMetric?: { value: number; trend: 'up' | 'down' | 'neutral' } | null) => {
    if (!trendMetric) return undefined;
    return { value: trendMetric.value, trend: trendMetric.trend, period: 'vs last month' as const };
  }, []);

  // Chart Theme Configuration
  const isDark = theme === 'dark';
  const { gridColor, textColor, tooltipBg, tooltipColor, tooltipBorder } = getChartTheme(isDark);
  const pieColors = isDark ? DARK_COLORS : COLORS;

  const handleLogClick = (log: ActivityLog) => {
    // Prefer direct URL if available
    if (log.url) {
      navigate(log.url);
      return;
    }

    // Fallback to entityId/entityType navigation
    if (log.entityId && log.entityType) {
      switch (log.entityType) {
        case 'workOrder':
          navigateWithReturn(`/app/maintenance`, { ticketId: log.entityId });
          break;
        case 'property':
          navigateWithReturn(`/app/properties/${log.entityId}`);
          break;
        case 'tenant':
          navigateWithReturn(`/app/tenants/${log.entityId}`);
          break;
        case 'vendor':
          navigateWithReturn(`/app/maintenance/vendors/${log.entityId}`);
          break;
        case 'owner':
          navigateWithReturn(`/app/owners/${log.entityId}`);
          break;
        case 'bill':
          navigateWithReturn(ROUTES.OPS_ACCOUNTING_BILLS, { billId: log.entityId });
          break;
        case 'receivable':
          navigateWithReturn(`/app/accounting/receivables`, { receivableId: log.entityId });
          break;
        case 'lease':
          navigateWithReturn(`/app/leasing/leases`, { leaseId: log.entityId });
          break;
        case 'application':
          navigateWithReturn(`/app/leasing/applications`, { applicationId: log.entityId });
          break;
      }
    }
  };

  const handleViewAllTickets = () => {
    navigateTo('/app/maintenance');
  };

  const handleViewAllReceivables = () => {
    navigateTo('/app/accounting/receivables');
  };

  const handleViewAllApplications = () => {
    navigateTo('/app/leasing/applications');
  };

  const handleViewAllLeases = () => {
    navigateTo('/app/leasing/leases');
  };



  if (loadingBills || loadingReceivables || loadingUnits || loadingTickets || loadingApplications || loadingLeases || loadingDashboardTrends || loadingTasks) {
    return (
      <div className="space-y-6 animate-fade-in pb-12 flex flex-col h-full w-full">
        <div className="shrink-0">
          <Skeleton variant="rectangular" height={32} width={300} className="mb-2" />
          <Skeleton variant="rectangular" height={20} width={400} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 shrink-0">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} variant="rectangular" height={120} />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 shrink-0">
          <Skeleton variant="rectangular" height={400} className="lg:col-span-2" />
          <Skeleton variant="rectangular" height={400} />
        </div>
      </div>
    );
  }

  const billsReceivablesLoadFailed = billsError || receivablesError;

  return (
    <>
    <div className="space-y-6 animate-fade-in pb-12 flex flex-col h-full w-full">

      {billsReceivablesLoadFailed ? (
        <div
          role="alert"
          className="shrink-0 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200"
        >
          <p className="mb-3">
            Could not load bills or receivables. Portfolio financial metrics below may be incomplete until
            you retry.
          </p>
          <button
            type="button"
            className="rounded-md border border-red-300 bg-white px-3 py-1.5 text-sm font-medium text-red-800 hover:bg-red-50 dark:border-red-700 dark:bg-red-950 dark:text-red-100 dark:hover:bg-red-900/40"
            onClick={() => {
              void refetchBills();
              void refetchReceivables();
            }}
          >
            Retry
          </button>
        </div>
      ) : null}

      {/* Header & Filter */}
      <div className="shrink-0">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Portfolio Overview</h1>
            <p className="text-slate-500 dark:text-slate-400">Real-time system activity and financial health.</p>
          </div>
          <div className="text-sm text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 px-3 py-1.5 rounded-lg border border-emerald-100 dark:border-emerald-800 shadow-sm flex items-center font-bold">
            <div className="w-2 h-2 bg-emerald-500 rounded-full mr-2 animate-pulse shadow-[0_0_8px_rgba(16,185,129,0.6)]"></div>
            System Live
          </div>
        </div>

        <FilterBar
          searchValue={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Quick jump..."
          tabs={[
            { label: 'YTD', value: 'YTD' },
            { label: 'Last 6 Months', value: '6M' },
            { label: 'Last 12 Months', value: '12M' },
            { label: 'Last 18 Months', value: '18M' },
            { label: 'This Quarter', value: 'QTD' },
            { label: 'Last 30 Days', value: '30D' },
          ]}
          activeTab={dateRange}
          onTabChange={setDateRange}
        />
      </div>

      {showGettingStarted && (
        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm ring-1 ring-slate-200/50 dark:ring-slate-700/50 overflow-hidden mb-6">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-800">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white uppercase tracking-wide">Getting Started</h3>
            <button
              type="button"
              onClick={dismissGettingStarted}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:text-slate-300 dark:hover:bg-slate-800 transition"
              aria-label="Dismiss"
            >
              <X size={18} />
            </button>
          </div>
          <div className="p-4">
            <ul className="space-y-2">
              <li>
                <button
                  type="button"
                  onClick={() => { navigate(`${ROUTES.PROPERTIES}?openAddPropertyWizard=1`); }}
                  className="flex items-center gap-3 w-full text-left p-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition"
                >
                  <div className="w-9 h-9 rounded-lg bg-brand-100 dark:bg-brand-900/30 text-brand-600 dark:text-brand-400 flex items-center justify-center shrink-0">
                    <Building size={18} />
                  </div>
                  <div>
                    <span className="font-medium text-slate-900 dark:text-white">Add your first property</span>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Set up a property to start managing units and leases.</p>
                  </div>
                  <ArrowRight size={16} className="ml-auto text-slate-400 shrink-0" />
                </button>
              </li>
            </ul>
          </div>
        </div>
      )}

      {/* Operations-first command row */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <button
          type="button"
          onClick={() => navigate('/app/pm/work-queue')}
          className="rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:hover:bg-slate-800"
        >
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Needs Attention Today</p>
          <p className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">{dueTodayTasks + pendingApplications}</p>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Queue items due now</p>
        </button>
        <button
          type="button"
          onClick={() => navigate('/app/pm/work-queue')}
          className="rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:hover:bg-slate-800"
        >
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Overdue Follow-ups</p>
          <p className="mt-2 text-2xl font-bold text-amber-600 dark:text-amber-400">{overdueFollowups}</p>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Open tasks past due date</p>
        </button>
        <button
          type="button"
          onClick={() => navigate('/app/pm/exceptions')}
          className="rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:hover:bg-slate-800"
        >
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Exceptions</p>
          <p
            className="mt-2 text-2xl font-bold text-red-600 dark:text-red-400"
            data-testid="pm-dashboard-open-exception-total"
          >
            {exceptionCount}
          </p>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Financial + compliance blockers</p>
        </button>
        <button
          type="button"
          onClick={() => navigate('/app/accounting/close')}
          className="rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:hover:bg-slate-800"
        >
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Close Readiness</p>
          <p className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">{closeReadiness}%</p>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Checklist gates passing</p>
        </button>
      </div>
      {propertiesKnownEmpty && (
        <div className="rounded-xl border border-brand-200 dark:border-brand-800 bg-brand-50/50 dark:bg-brand-900/20 p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <p className="text-sm text-slate-700 dark:text-slate-300">{PORTAL_EMPTY_STATES.pm.gettingStartedHint}</p>
          <button
            type="button"
            onClick={() => navigate(`${ROUTES.PROPERTIES}?openAddPropertyWizard=1`)}
            className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 shrink-0"
          >
            <Building size={16} />
            Add your first property
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <h3 className="font-semibold text-slate-900 dark:text-white">Close Checklist Gates</h3>
          <div className="mt-3 space-y-2">
            {closeChecklistGates.map((gate) => (
              <div key={gate.label} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2 dark:border-slate-800">
                <span className="text-sm text-slate-600 dark:text-slate-300">{gate.label}</span>
                <span className={`text-xs font-semibold ${gate.pass ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
                  {gate.pass ? 'Pass' : 'Action Needed'}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <h3 className="font-semibold text-slate-900 dark:text-white">Follow-up Automation</h3>
          <p className="mt-2 text-3xl font-bold text-brand-600 dark:text-brand-400">{followupAutomationCoverage}%</p>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Active workflows covering follow-ups, renewals, and delinquency actions.
          </p>
          <button
            type="button"
            onClick={() => navigate('/app/workflows')}
            className="mt-3 inline-flex items-center gap-2 rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700"
          >
            Manage Templates
            <ArrowRight size={14} />
          </button>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <h3 className="font-semibold text-slate-900 dark:text-white">PM Productivity Metrics</h3>
          <div className="mt-3 space-y-2">
            <div className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2 dark:border-slate-800">
              <span className="text-sm text-slate-600 dark:text-slate-300">Queue aging (days)</span>
              <span className="text-sm font-semibold text-slate-900 dark:text-white">{avgQueueAgingDays}</span>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2 dark:border-slate-800">
              <span className="text-sm text-slate-600 dark:text-slate-300">Exception SLA risk</span>
              <span className="text-sm font-semibold text-slate-900 dark:text-white">{exceptionCount}</span>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2 dark:border-slate-800">
              <span className="text-sm text-slate-600 dark:text-slate-300">Today queue size</span>
              <span className="text-sm font-semibold text-slate-900 dark:text-white">{dueTodayTasks + pendingApplications}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 shrink-0">
        <StatCard
          title="Total Revenue"
          value={formatCurrency(totalRevenue, { showCents: false })}
          change={getTrendChange(dashboardTrends?.revenue)}
          icon={DollarSign}
          color="green"
        />
        <StatCard
          title="Occupancy Rate"
          value={`${occupancyRate}%`}
          change={getTrendChange(dashboardTrends?.occupancy)}
          icon={Home}
          color="blue"
        />
        <StatCard
          title="Open Work Orders"
          value={openTickets}
          change={getTrendChange(dashboardTrends?.openTickets)}
          icon={AlertCircle}
          color="amber"
          onClick={handleViewAllTickets}
        />
        <StatCard
          title="Completed Work Orders"
          value={completedTickets}
          change={getTrendChange(dashboardTrends?.completedTickets)}
          icon={CheckCircle}
          color="purple"
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 shrink-0">
        <StatCard
          title="Pending Applications"
          value={pendingApplications}
          change={getTrendChange(dashboardTrends?.pendingApplications)}
          icon={FileCheck}
          color="indigo"
          onClick={handleViewAllApplications}
        />
        <StatCard
          title="Active Leases"
          value={activeLeases}
          change={getTrendChange(dashboardTrends?.activeLeases)}
          icon={Key}
          color="blue"
          onClick={handleViewAllLeases}
        />
      </div>

      {/* Compliance Widget */}
      {loadingHCVPrograms ? (
        <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900 dark:text-white flex items-center gap-2">
              <ShieldAlert size={20} className="text-brand-600 dark:text-brand-400" />
              Compliance Status
            </h3>
          </div>
          <div className="grid grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, idx) => (
              <div key={idx} className="space-y-2">
                <Skeleton variant="rectangular" height={16} width="60%" />
                <Skeleton variant="rectangular" height={32} width="40%" />
              </div>
            ))}
          </div>
        </div>
      ) : hcvPrograms.length > 0 && (
        <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900 dark:text-white flex items-center gap-2">
              <ShieldAlert size={20} className="text-brand-600 dark:text-brand-400" />
              Compliance Status
            </h3>
            <button
              onClick={() => navigate('/app/compliance')}
              className="text-xs text-brand-600 dark:text-brand-400 hover:underline font-medium"
            >
              View Details →
            </button>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <p className="text-sm text-slate-600 dark:text-slate-400">Active Vouchers</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{hcvPrograms.length}</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-slate-600 dark:text-slate-400">Inspections Due</p>
              <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                {hcvPrograms.filter(p => p.inspectionStatus === 'Scheduled' || p.inspectionStatus === 'Pending').length}
              </p>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-slate-600 dark:text-slate-400">At Risk</p>
              <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                {hcvPrograms.filter(p => p.complianceStatus === 'At Risk').length}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 shrink-0">
        {/* Main Chart Section */}
        <div className="lg:col-span-2 bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm min-w-0 flex flex-col">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white">Revenue vs Expenses</h3>
            <span
              className="text-xs text-brand-600 dark:text-brand-400 font-medium cursor-pointer hover:underline"
              onClick={handleViewAllReceivables}
            >
              View All
            </span>
          </div>
          <div className="w-full h-[400px]">
            {financialData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={financialData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={BRAND_COLOR} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={BRAND_COLOR} stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorValue2" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={ACCENT_COLOR} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={ACCENT_COLOR} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={gridColor} strokeOpacity={0.5} />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: textColor, fontSize: 12 }} />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: textColor, fontSize: 12 }}
                    tickFormatter={formatCompactCurrency}
                  />
                  <Tooltip
                    contentStyle={{ backgroundColor: tooltipBg, borderRadius: '8px', border: tooltipBorder, boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', color: tooltipColor }}
                    itemStyle={{ color: tooltipColor }}
                    cursor={{ stroke: textColor, strokeWidth: 1, strokeDasharray: '3 3' }}
                    formatter={(value: number) => formatCompactCurrency(value)}
                  />
                  <Area type="monotone" dataKey="value" name="Income" stroke={BRAND_COLOR} strokeWidth={3} fillOpacity={1} fill="url(#colorValue)" />
                  <Area type="monotone" dataKey="value2" name="Expenses" stroke={ACCENT_COLOR} strokeWidth={3} fillOpacity={1} fill="url(#colorValue2)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-full items-center justify-center text-slate-400 flex-col">
                <Activity size={48} className="mb-2 opacity-50" />
                <p>No financial data available for this period.</p>
              </div>
            )}
          </div>
        </div>

        {/* System Activity Feed */}
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col min-w-0 h-[400px] overflow-hidden">
          <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 flex justify-between items-center">
            <h3 className="font-bold text-slate-900 dark:text-white">System Activity</h3>
          </div>
          <div className="overflow-y-auto custom-scrollbar flex-1">
            {loadingActivityLogs ? (
              <LoadingSpinner size="md" className="h-full" />
            ) : activityLogs.length === 0 ? (
              <EmptyState
                icon={Activity}
                title="No Activity Yet"
                message="System activity will appear here as you use the platform."
                className="p-8"
              />
            ) : (
              activityLogs.map((log) => (
                <ActivityItem key={log.id} log={log} onClick={() => handleLogClick(log)} />
              ))
            )}
          </div>
        </div>
      </div>

      {/* Occupancy Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 shrink-0 relative">
        <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col min-w-0 relative z-0">
          <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Occupancy Status</h3>
          <div className="w-full h-[200px] relative min-w-0" style={{ minHeight: '200px' }}>
            {loadingUnits ? (
              <div className="flex h-full items-center justify-center text-slate-400">Loading…</div>
            ) : occupancyData.length === 0 ? (
              <EmptyState
                icon={Home}
                title="No units yet"
                message="Add a property and units to see occupancy here."
                action={{
                  label: 'Add property',
                  onClick: () => navigate(`${ROUTES.PROPERTIES}?openAddPropertyWizard=1`),
                }}
              />
            ) : (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={occupancyData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                      stroke="none"
                    >
                      {occupancyData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ backgroundColor: tooltipBg, borderRadius: '8px', border: tooltipBorder, boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', color: tooltipColor }}
                      itemStyle={{ color: tooltipColor }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none flex-col">
                  <span className="block text-3xl font-bold text-slate-900 dark:text-white">{occupancyRate}%</span>
                  <span className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide font-bold">Occupied</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Quick Actions / Shortcuts - AUTOMATION HUB */}
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 dark:from-slate-800 dark:to-slate-900 p-6 rounded-xl border border-slate-700 shadow-lg text-white flex flex-col justify-center relative z-0 overflow-hidden">
          <div className="absolute top-0 right-0 p-32 bg-brand-500/10 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none z-0"></div>

          <div className="relative z-1">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-bold flex items-center"><Zap size={18} className="mr-2 text-yellow-400" /> Automation Hub</h3>
              <div className="flex items-center space-x-1">
                <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
                <span className="text-xs text-emerald-400 font-bold uppercase">Online</span>
              </div>
            </div>
            <p className="text-slate-300 text-sm mb-6">
              {isLoadingWorkflows ? 'Loading workflows...' : `${activeWorkflows.length} workflows active. AI is monitoring payments, maintenance, and renewals.`}
            </p>
            <div className="space-y-3">
              {activeWorkflows.slice(0, 2).map((wf: Workflow) => (
                <div key={wf.id} className="flex items-center justify-between bg-white/5 hover:bg-white/10 p-3 rounded-lg border border-white/10 transition cursor-pointer group">
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-emerald-400 rounded-full mr-3 shadow-[0_0_8px_rgba(52,211,153,0.6)]"></div>
                    <span className="text-sm font-medium group-hover:text-white transition">{wf.title}</span>
                  </div>
                  <span className="text-xs text-slate-400 flex items-center">
                    {wf.executionCount} runs <ArrowRight size={10} className="ml-1 opacity-0 group-hover:opacity-100 transition" />
                  </span>
                </div>
              ))}
            </div>
            <button
              onClick={() => navigateTo('/app/workflows')}
              className="mt-6 w-full py-2 bg-white/10 hover:bg-white/20 text-xs font-bold uppercase tracking-wider rounded-lg transition-all focus:ring-4 focus:ring-white/10 focus:scale-[1.02] active:scale-[0.98] outline-none"
            >
              Configure Workflows
            </button>
            {activeWorkflows.some((w: Workflow) => w.title.toLowerCase().includes('late fee') || w.title.toLowerCase().includes('late')) && (
              <button
                onClick={() => navigate('/app/workflows')}
                className="mt-3 w-full py-2 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-300 text-xs font-bold uppercase tracking-wider rounded-lg transition-all border border-yellow-500/30 focus:ring-4 focus:ring-yellow-500/10 focus:scale-[1.02] active:scale-[0.98] outline-none"
              >
                Late Fee Automation
              </button>
            )}
          </div>
        </div>
      </div>
    </div>

    {firstRunWizardOpen && (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in px-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            setFirstRunWizardOpen(false);
            try {
              if (currentUserId) localStorage.setItem(`${FIRST_RUN_WIZARD_DISMISSED_KEY}-${currentUserId}`, 'true');
            } catch { /* ignore */ }
            setFirstRunWizardDismissed(true);
          }
        }}
      >
        <div
          className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col border border-slate-200 dark:border-slate-800 animate-slide-up"
          onClick={(e) => e.stopPropagation()}
        >
          <AddPropertyWizard
            onClose={() => {
              setFirstRunWizardOpen(false);
              try {
                if (currentUserId) localStorage.setItem(`${FIRST_RUN_WIZARD_DISMISSED_KEY}-${currentUserId}`, 'true');
              } catch { /* ignore */ }
              setFirstRunWizardDismissed(true);
            }}
            onSuccess={(propertyId) => {
              setFirstRunWizardOpen(false);
              queryClient.invalidateQueries({ queryKey: ['properties'] });
              queryClient.invalidateQueries({ queryKey: ['units'] });
              navigate(`${ROUTES.PROPERTY_DETAIL.replace(':propertyId', propertyId)}`);
            }}
          />
        </div>
      </div>
    )}
    </>
  );
};



